// Pure selectors that turn Ops query data into desk module face props.
// No React, no DOM: every function tolerates undefined/missing input and
// returns null/empty fields rather than throwing or leaking NaN. Server
// timestamps go through parseServerTime (naive ISO strings are UTC, ruling 40);
// `now` is passed in wherever time matters.
import type {
  AgentHealth24hData,
  AgentRow,
  CloudWatchData,
  ControlsStatus,
  AdaptiveStatus,
  HealthData,
  Goal,
  LLMEnvelope,
  RiskMetrics,
  ServicesData,
  SignalPipelineData,
  VetoRates,
} from '../../api/client'
import { adaptiveModeWord } from '../../lib/adaptiveMode'
import { parseServerTime } from '../../lib/relativeTime'
import type { BookStatus, EngineStatus, EnsembleStatus } from '../../types'
import type { SanityEvt } from '../../hooks/useSanityGateBuffer'
import type { FunnelStage } from '../viz/MiniFunnel'
import type { HeatCell } from '../viz/HeatGrid'
import { clamp01 } from '../viz/geometry'
import { num } from './fleetSummaries'

export type Pip = 'ok' | 'fail' | 'warn' | 'empty' | 'off'

const MINUTE_MS = 60_000
const HOUR_MS = 3_600_000
/** A system-health `last_updated` older than this reads as stale. */
export const MARKET_STALE_MS = 5 * MINUTE_MS

const grouped = (n: number) => n.toLocaleString('en-US')

// -------------------------------------------------------------------- engine

/** One pip per engine book: halted → `fail`, else `ok` (ruling 47). */
export function bookPips(books: BookStatus[] | undefined): { pips: Pip[]; halted: number } {
  const list = books ?? []
  return {
    pips: list.map((b): Pip => (b.halted ? 'fail' : 'ok')),
    halted: list.filter((b) => b.halted).length,
  }
}

/** Largest whole unit since `startedAt`: `25m` (< 1h), `40h` (< 72h), else `3d`. Null when unknown. */
export function uptimeShort(startedAt: string | null | undefined, now: number): string | null {
  const t = parseServerTime(startedAt)
  if (t === null) return null
  const ms = Math.max(0, now - t)
  if (ms < HOUR_MS) return `${Math.floor(ms / MINUTE_MS)}m`
  const h = Math.floor(ms / HOUR_MS)
  return h < 72 ? `${h}h` : `${Math.floor(h / 24)}d`
}

/**
 * Ruling K: the engine's run counters persist across an in-container stop→start
 * (load_state) while `started_at` resets, so `since <uptime>` can under-state the
 * window. When the total exceeds what the uptime allows (`ceil(hours) × 13 + 13`)
 * the window is `deploy` (counters reset only with a new container); otherwise
 * the uptime. Unknown total keeps the uptime; null when the start is unknown.
 */
export function runsWindow(total: number | null, startedAt: string | null | undefined, now: number): string | null {
  const uptime = uptimeShort(startedAt, now)
  if (uptime === null) return null
  const t = parseServerTime(startedAt)
  if (total !== null && t !== null) {
    const hours = Math.max(0, now - t) / HOUR_MS
    if (total > Math.ceil(hours) * 13 + 13) return 'deploy'
  }
  return uptime
}

// ------------------------------------------------------------------ services

/** Up = connected with a closed circuit. Null while unknown or when no service is registered. */
export function servicesFace(d: ServicesData | undefined): {
  up: number
  total: number
  down: string[]
  failures: number
  pips: Pip[]
} | null {
  const list = d?.services
  if (!Array.isArray(list) || list.length === 0) return null
  const isUp = (s: ServicesData['services'][number]) => s.connected && s.circuit_state === 'closed'
  return {
    up: list.filter(isUp).length,
    total: list.length,
    down: list.filter((s) => !isUp(s)).map((s) => s.name),
    failures: list.reduce((sum, s) => sum + (num(s.failure_count) ?? 0), 0),
    pips: list.map((s): Pip => (!s.connected ? 'fail' : s.circuit_state !== 'closed' ? 'warn' : 'ok')),
  }
}

// ---------------------------------------------------------------- cloudwatch

/**
 * The newest point of one CloudWatch metric plus its whole series, ordered by
 * PARSED timestamp (the API sends newest-first; never trust array order).
 * Unparseable timestamps and non-finite values are dropped pairwise. Null when
 * the metric is missing, has no valid point, or CloudWatch is unavailable —
 * never a 0.
 */
export function cloudwatchLatest(
  d: CloudWatchData | undefined,
  key: string,
): { value: number; series: number[] } | null {
  if (!d || d.available === false) return null
  const m = d.metrics?.[key]
  if (!m || !Array.isArray(m.timestamps) || !Array.isArray(m.values)) return null
  const points: Array<{ t: number; v: number }> = []
  for (let i = 0; i < Math.min(m.timestamps.length, m.values.length); i++) {
    const t = parseServerTime(m.timestamps[i])
    const raw = m.values[i]
    if (t === null || typeof raw !== 'number' || !Number.isFinite(raw)) continue
    points.push({ t, v: raw })
  }
  if (points.length === 0) return null
  points.sort((a, b) => a.t - b.t)
  return { value: points[points.length - 1].v, series: points.map((p) => p.v) }
}

// -------------------------------------------------------------------- agents

const lower = (s: unknown) => (typeof s === 'string' ? s.toLowerCase() : '')

/** The API's placeholder status when the orchestrator is absent (system.py): neutral, not a failure. */
const isNotConfigured = (status: unknown) => lower(status) === 'not_configured'
/** Heat-cell text for a `not_configured` runtime agent. */
export const NOT_CONFIGURED = 'not configured'

/**
 * Runtime agents as pips: healthy → `ok`, degraded/warning → `warn`, not_configured → `off`
 * (neutral: the orchestrator is absent, nothing failed), anything else → `fail`. Case-insensitive.
 */
export function agentsFace(rows: AgentRow[] | undefined): {
  healthy: number
  total: number
  notConfigured: number
  pips: Pip[]
} | null {
  if (!Array.isArray(rows) || rows.length === 0) return null
  const pip = (r: AgentRow): Pip => {
    const s = lower(r.status)
    return s === 'healthy'
      ? 'ok'
      : s === 'degraded' || s === 'warning'
        ? 'warn'
        : isNotConfigured(s)
          ? 'off'
          : 'fail'
  }
  const pips = rows.map(pip)
  return {
    healthy: pips.filter((p) => p === 'ok').length,
    total: rows.length,
    notConfigured: pips.filter((p) => p === 'off').length,
    pips,
  }
}

/** Σ rejects / total_events as a FRACTION (0–1; the face formats it). Null when there were no events. */
export function vetoRate(v: VetoRates | undefined): number | null {
  if (!v) return null
  const total = num(v.total_events)
  if (total === null || total <= 0) return null
  const rejects = (v.rules ?? []).reduce((sum, r) => sum + (num(r.rejects) ?? 0), 0)
  return rejects / total
}

// -------------------------------------------------------------------- market

/** Market status word and the age of the health reading (`stale` past 5 min; false when the age is unknown). */
export function marketFace(
  h: HealthData | undefined,
  now: number,
): { word: string; ageMs: number | null; stale: boolean } | null {
  if (!h) return null
  const t = parseServerTime(h.last_updated)
  const ageMs = t === null ? null : Math.max(0, now - t)
  const status = typeof h.market_status === 'string' && h.market_status ? h.market_status : null
  return {
    word: status ? status.replace(/_/g, ' ').toUpperCase() : '—',
    ageMs,
    stale: ageMs !== null && ageMs > MARKET_STALE_MS,
  }
}

// -------------------------------------------------------------- agent × state

/**
 * One cell per agent: the union of the runtime agents (API order) and the 24h
 * decision agents (appended when runtime does not list them), matched by
 * lower-cased name. Value −1 when the runtime status is not healthy, +1 when
 * healthy-or-unknown and active (decisions > 0 or tasksProcessed > 0), else 0.
 * The decision display (`1,950 · 0.75`) wins when both lists have the agent.
 */
export function agentCells(
  runtime: AgentRow[] | undefined,
  decisions: AgentHealth24hData | undefined,
): HeatCell[] {
  const decided = new Map<string, AgentHealth24hData['agents'][number]>()
  for (const a of decisions?.agents ?? []) decided.set(lower(a.agent_name), a)
  const decisionDisplay = (a: AgentHealth24hData['agents'][number]) =>
    `${grouped(num(a.decisions) ?? 0)} · ${(num(a.avg_confidence) ?? 0).toFixed(2)}`

  const cells: HeatCell[] = []
  const seen = new Set<string>()
  for (const r of runtime ?? []) {
    const key = lower(r.name)
    seen.add(key)
    const d = decided.get(key)
    const healthy = lower(r.status) === 'healthy'
    const unconfigured = isNotConfigured(r.status)
    const tasks = num(r.tasksProcessed) ?? 0
    const active = (d ? (num(d.decisions) ?? 0) > 0 : false) || tasks > 0
    // not_configured is neutral (0), never the loss of a failed agent.
    const value = unconfigured ? 0 : !healthy ? -1 : active ? 1 : 0
    const display = d
      ? decisionDisplay(d)
      : unconfigured
        ? NOT_CONFIGURED
        : healthy
        ? `healthy · ${tasks > 0 ? `${grouped(tasks)} ${tasks === 1 ? 'task' : 'tasks'}` : 'idle'}`
        : String(r.status)
    cells.push({ label: r.name, value, display })
  }
  for (const [key, d] of decided) {
    if (seen.has(key)) continue
    cells.push({
      label: d.agent_name,
      value: (num(d.decisions) ?? 0) > 0 ? 1 : 0,
      display: decisionDisplay(d),
    })
  }
  return cells
}

// ------------------------------------------------------------------ pipeline

/**
 * Ruling 48: the session counts when the session is current and received > 0,
 * else the cumulative counts (foot `since reset · none this session`).
 */
export function pipelineFace(p: SignalPipelineData | undefined): {
  stages: FunnelStage[]
  window: 'session' | 'cumulative'
  foot: string[]
} | null {
  if (!p) return null
  const s = p.session
  const useSession = !!s && s.is_current === true && (num(s.received) ?? 0) > 0
  const src = useSession && s ? s : p
  const stages: FunnelStage[] = [
    { label: 'Received', count: num(src.received) ?? 0 },
    { label: 'Queued', count: num(src.queued) ?? 0 },
    { label: 'Ordered', count: num(src.ordered) ?? 0 },
    { label: 'Executed', count: num(src.executed) ?? 0, tone: 'gain' },
    { label: 'Rejected', count: num(src.rejected) ?? 0, tone: 'warn' },
  ]
  const foot = [useSession && s ? `session ${s.session_date.slice(5)}` : 'since reset · none this session']
  const top = (src.reject_reasons ?? []).reduce<SignalPipelineData['reject_reasons'][number] | null>(
    (best, r) => ((num(r.count) ?? 0) > (best ? (num(best.count) ?? 0) : 0) ? r : best),
    null,
  )
  if (top) foot.push(`top reject ${top.label} ${grouped(num(top.count) ?? 0)}`)
  return { stages, window: useSession ? 'session' : 'cumulative', foot }
}

// ---------------------------------------------------------------- sanity gate

const GATE_COLORS: Record<string, string> = {
  allow: 'var(--gain)',
  modify: 'var(--warn)',
  reject: 'var(--loss)',
}
const GATE_ORDER = ['allow', 'modify', 'reject']

/** Decision counts for the donut (ruling 49): allow, modify, reject, then others by count. Empty for no events. */
export function gateSlices(events: SanityEvt[]): Array<{ label: string; value: number; color: string }> {
  const counts = new Map<string, number>()
  for (const e of events) {
    const d = lower(e.decision) || 'unknown'
    counts.set(d, (counts.get(d) ?? 0) + 1)
  }
  const slice = (label: string) => ({
    label,
    value: counts.get(label) ?? 0,
    color: GATE_COLORS[label] ?? 'var(--fg-subtle)',
  })
  const known = GATE_ORDER.filter((l) => counts.has(l)).map(slice)
  const others = [...counts.keys()]
    .filter((l) => !GATE_ORDER.includes(l))
    .map(slice)
    .sort((a, b) => b.value - a.value)
  return [...known, ...others]
}

// ---------------------------------------------------------------------- risk

export interface RiskRow {
  label: string
  text: string
  ratio: number
  /** False for a row that is a reading, not a limit (Exposure, Ruling J): never the peak, never loss-coloured. */
  isLimit: boolean
}

/**
 * The rows behind the gauge (ruling 50, amended by Ruling J), in the order Total
 * DD, Daily DD, Exposure, Positions, Leverage. A pair whose limit is not a finite
 * number > 0, or whose value is missing, is omitted. `ratio` is value / limit and
 * is NOT clamped (an over-cap 1.0004 is the truth; the arc clamps). Exposure is
 * not a limit: /risk/metrics hardcodes max_exposure 100.0 ("Full portfolio") and
 * the research book is fully invested by design, so it reads `100.4% of equity`
 * and is marked `isLimit: false`.
 */
export function riskRows(m: RiskMetrics | undefined): RiskRow[] {
  if (!m) return []
  const rows: RiskRow[] = []
  const add = (
    label: string,
    value: unknown,
    limit: unknown,
    text: (v: number, l: number) => string,
    isLimit = true,
  ) => {
    const v = num(value)
    const l = num(limit)
    if (v === null || l === null || l <= 0) return
    rows.push({ label, text: text(v, l), ratio: v / l, isLimit })
  }
  const percent = (v: number, l: number) => `${v.toFixed(1)} / ${Number(l.toFixed(1))}%`
  add('Total DD', m.total_drawdown, m.total_drawdown_limit, percent)
  add('Daily DD', m.daily_drawdown, m.daily_drawdown_limit, percent)
  add('Exposure', m.portfolio_exposure, m.max_exposure, (v) => `${v.toFixed(1)}% of equity`, false)
  add('Positions', m.position_count, m.max_position_count, (v, l) => `${v} / ${l}`)
  add('Leverage', m.gross_leverage, m.max_gross_leverage, (v, l) => `${v.toFixed(2)} / ${l.toFixed(2)}×`)
  return rows
}

/** The limit row with the highest ratio (first on a tie); non-limit rows never peak. Null when there are none. */
export function peakRow(rows: RiskRow[]): RiskRow | null {
  return rows.reduce<RiskRow | null>(
    (best, r) => (!r.isLimit || (best !== null && r.ratio <= best.ratio) ? best : r),
    null,
  )
}

export function riskTone(ratio: number): 'brand' | 'warn' | 'loss' {
  return ratio > 1 ? 'loss' : ratio >= 0.8 ? 'warn' : 'brand'
}

// --------------------------------------------------------------------- goals

/**
 * Active goals in API order (ruling 45). A goal never reconciled (`updatedAt`
 * missing or equal to `createdAt`) reads `not yet computed` with a null ratio,
 * not a clean 0. `ratio = clamp01(current / target)` when target > 0, else null.
 */
export function goalRows(goals: Goal[] | undefined): Array<{
  name: string
  text: string
  ratio: number | null
  negative: boolean
}> {
  return (goals ?? [])
    .filter((g) => g.active)
    .map((g) => {
      // Instants, not strings: the same moment spelled with and without an
      // offset is still "never reconciled"; a missing or unparseable stamp is too.
      const updated = parseServerTime(g.updatedAt)
      const created = parseServerTime(g.createdAt)
      const reconciled = updated !== null && created !== null && updated !== created
      const current = num(g.current)
      const target = num(g.target)
      if (!reconciled || current === null || target === null) {
        return { name: g.name, text: 'not yet computed', ratio: null, negative: false }
      }
      const unit = g.unit ? (g.unit === '%' ? '%' : ` ${g.unit}`) : ''
      return {
        name: g.name,
        text: `${current.toFixed(1)} / ${Number(target.toFixed(1))}${unit}`,
        ratio: target > 0 ? clamp01(current / target) : null,
        negative: current < 0,
      }
    })
}

// -------------------------------------------------------------------- models

/** Blended ML recommendation, or `PIPELINE OFF` (warn) when the pipeline is disabled. */
export function ensembleFace(e: EnsembleStatus | undefined): {
  word: string
  tone: 'neutral' | 'warn'
  detail: string
  agreement: number | null
  providers: number
  updated: string | null
} | null {
  if (!e) return null
  const score = num(e.blended?.combined_score)
  const agreement = num(e.blended?.agreement_level)
  const rec = e.blended?.recommendation
  const off = e.pipeline_enabled === false
  // A disabled pipeline's blended score is a leftover, not a reading: say so.
  const reading = [
    score === null ? null : `score ${score >= 0 ? '+' : '-'}${Math.abs(score).toFixed(2)}`,
    agreement === null ? null : `agree ${(agreement * 100).toFixed(0)}%`,
  ]
    .filter((x): x is string => x !== null)
    .join(' · ')
  const detail = off ? 'pipeline disabled' : reading
  return {
    word: off ? 'PIPELINE OFF' : rec ? rec.replace(/_/g, ' ').toUpperCase() : '—',
    tone: off ? 'warn' : 'neutral',
    detail,
    agreement: off ? null : agreement,
    providers: Array.isArray(e.providers) ? e.providers.length : 0,
    updated: e.last_updated || null,
  }
}

// ------------------------------------------------------------------ operator

/** The four state rows of the `operator` face (ruling 46); a missing source reads `—`. */
export function operatorRows(
  engine: EngineStatus | undefined,
  controls: ControlsStatus | undefined,
  adaptive: AdaptiveStatus | undefined,
  envelope: LLMEnvelope | undefined,
): Array<{
  label: 'Engine' | 'External APIs' | 'Adaptive' | 'LLM cadence'
  word: string
  tone: 'gain' | 'warn' | 'neutral'
}> {
  return [
    {
      label: 'Engine',
      word: engine ? String(engine.status).toUpperCase() : '—',
      tone: engine?.status === 'running' ? 'gain' : 'neutral',
    },
    {
      label: 'External APIs',
      word: controls ? (controls.external_apis_paused ? 'PAUSED' : 'LIVE') : '—',
      tone: controls?.external_apis_paused ? 'warn' : 'neutral',
    },
    {
      label: 'Adaptive',
      // NOT `adaptive.mode`: that is the API container's silent default, which
      // read "OFF" for a loop running in "full". adaptiveModeWord falls back to
      // what recorded runs prove.
      word: adaptive ? adaptiveModeWord(adaptive) : '—',
      tone: 'neutral',
    },
    {
      label: 'LLM cadence',
      word: envelope ? (envelope.cadence_enabled ? 'ON' : 'OFF') : '—',
      tone: 'neutral',
    },
  ]
}
